/**
 * @file Boss errant et Raid : combats de boss a phases.
 *  - Boss errant : 3 phases (PV 100 -> 66 -> 33 -> 0 %). Aux deux seuils le golem se couvre d'une carapace (invulnerable et non ciblable) et
 *    fait apparaitre des pylones de cristal ; il redevient vulnerable quand tous sont detruits, plus rapide et plus charge. Le niveau du boss
 *    est le nombre de victoires (PV x levelHpGrowth par niveau). Score = phases + temps restant ; recompenses : gemmes, StarGem, tirages d'armes.
 *  - Raid : dragonnet de saison, PV x bossHpPerLevel par niveau, 3 phases ; des la phase 2 il pond des oeufs qui eclosent (et le soignent)
 *    s'ils ne sont pas detruits a temps. Score = niveaux de PV retires ; recompenses : StarGem + tirage epique.
 */
import { stageHp } from '../../balance/formulas';
import { spawnTarget } from '../../ecs/EntityFactory';
import type { SimContext } from '../../Context';
import type { With } from '../../ecs/World';
import { clampToArena } from '../../math/geometry';
import type { ModeHud, ModeResult, ModeStartParams } from '../ModeTypes';
import { GenericCombatMode } from './SkeletonModes';

/** Duree (s) d'invulnerabilite posee pendant une carapace : elle est levee explicitement a la mort du dernier pylone. */
const SHIELD_FOREVER = 3600;

/** Cibles vivantes (non mourantes) d'un type donne. */
const aliveOf = (ctx: SimContext, defId: string): Array<With<'target' | 'health'>> =>
  [...ctx.rt.world.ofKind('target')].filter((e): e is With<'target' | 'health'> => e.target?.defId === defId && e.alive && e.health !== undefined && (e.visual?.dying ?? 0) === 0);

const hpRatio = (e: With<'target' | 'health'>): number => (e.health.max.gt(0) ? Math.max(0, Math.min(1, e.health.hp.div(e.health.max).toNumber())) : 0);

// ======================================================================================================================
// Boss errant
// ======================================================================================================================

export class BossMode extends GenericCombatMode<'boss'> {
  /** Golem (PV x40) : ~5 arbres de la frontiere en PV, a battre en 90 s. */
  protected override tierOffset = 20;
  private bossId: number | null = null;
  /** Phase courante, 0-based (0 = avant la premiere carapace). */
  private bossPhase = 0;
  private shielded = false;

  /** Niveau du boss = victoires deja obtenues. */
  protected level(ctx: SimContext): number {
    return ctx.data.progress.modeBests.boss ?? 0;
  }

  private get totalPhases(): number {
    return this.params.phaseAt.length + 1;
  }

  protected override onEnter(ctx: SimContext, params: ModeStartParams): void {
    super.onEnter(ctx, params);
    this.bossPhase = 0;
    this.shielded = false;
    this.bossId = null;
  }

  protected override spawnInitial(ctx: SimContext): void {
    const p = this.params;
    const growth = Math.pow(p.levelHpGrowth, Math.min(this.level(ctx), p.maxScaleLevel));
    const boss = spawnTarget(ctx, ctx.index.target('boss_golem'), 0, 0, { stageValue: this.tierValue(ctx), hpMult: p.bossHpMult * growth, isBoss: true });
    this.bossId = boss.id;
    this.spawnedCount++;
    this.applyPhase(boss, 0);
  }

  /** Vitesse et frequence des charges de la phase `i`. */
  private applyPhase(boss: With<'target' | 'health'>, i: number): void {
    const p = this.params;
    const idx = Math.min(i, p.phaseSpeedMult.length - 1);
    if (boss.mover) boss.mover.speed = p.wanderSpeed * (p.phaseSpeedMult[idx] ?? 1);
    if (boss.ai) {
      boss.ai.params.baseSpeed = p.wanderSpeed * (p.phaseSpeedMult[idx] ?? 1);
      boss.ai.params.chargeEvery = p.chargeEvery[Math.min(i, p.chargeEvery.length - 1)] ?? 6;
    }
  }

  private boss(ctx: SimContext): With<'target' | 'health'> | null {
    const e = this.bossId === null ? undefined : ctx.rt.world.get(this.bossId);
    return e && e.alive && e.target && e.health ? (e as With<'target' | 'health'>) : null;
  }

  /** Franchissement d'un seuil : PV bornes au seuil, carapace + pylones (jamais de mort « a travers » une phase, meme avec un gros coup). */
  guardDamage(ctx: SimContext, target: With<'target' | 'health'>): void {
    if (target.id !== this.bossId || this.shielded || this.bossPhase >= this.params.phaseAt.length) return;
    const threshold = target.health.max.times(this.params.phaseAt[this.bossPhase] ?? 0);
    if (target.health.hp.lte(threshold)) {
      target.health.hp = threshold;
      this.startShield(ctx, target);
    }
  }

  private startShield(ctx: SimContext, boss: With<'target' | 'health'>): void {
    this.shielded = true;
    boss.target.shootable = false;
    boss.health.invulnerable = SHIELD_FOREVER;
    const count = this.params.pylonCount[this.bossPhase] ?? 3;
    const arena = ctx.modes.rules.arena;
    const radius = Math.max(3, Math.min(arena.halfW - 3.5, 8));
    const offset = ctx.rng.spawn.range(0, Math.PI * 2);
    for (let i = 0; i < count; i++) {
      const a = offset + (i / count) * Math.PI * 2;
      const pylon = spawnTarget(ctx, ctx.index.target('boss_pylon'), Math.cos(a) * radius, Math.sin(a) * radius, { stageValue: this.tierValue(ctx), hpMult: this.params.pylonHpMult });
      void pylon;
      this.spawnedCount++;
    }
    ctx.bus.emit('BossPhase', { mode: 'boss', phase: this.bossPhase + 1, phases: this.totalPhases, shield: true });
  }

  private endShield(ctx: SimContext, boss: With<'target' | 'health'>): void {
    this.shielded = false;
    boss.target.shootable = true;
    boss.health.invulnerable = 0;
    this.bossPhase++;
    this.applyPhase(boss, this.bossPhase);
    ctx.bus.emit('BossPhase', { mode: 'boss', phase: this.bossPhase + 1, phases: this.totalPhases, shield: false });
  }

  protected override onKill(ctx: SimContext, target: With<'target' | 'health'>): void {
    if (target.target.defId !== 'boss_pylon' || !this.shielded) return;
    if (aliveOf(ctx, 'boss_pylon').length > 0) return;
    const boss = this.boss(ctx);
    if (boss) this.endShield(ctx, boss);
  }

  protected override hudExtras(ctx: SimContext): Partial<ModeHud> {
    const boss = this.boss(ctx);
    return {
      boss: boss ? { hp: boss.health.hp, max: boss.health.max } : null,
      bossInfo: { level: this.level(ctx) + 1, phase: this.bossPhase + 1, phases: this.totalPhases, shield: this.shielded ? aliveOf(ctx, 'boss_pylon').length : 0, eggs: 0 },
    };
  }

  protected override buildRewards(ctx: SimContext, success: boolean): ModeResult['rewards'] {
    const level = this.level(ctx);
    const total = this.timeTotal ?? 1;
    const timeFrac = this.timeLeft !== null && total > 0 ? Math.max(0, Math.min(1, this.timeLeft / total)) : 0;
    if (success) {
      const sweep = this.sweeping;
      // score : 1000 par phase terminee + 10 par seconde restante + 100 par niveau (le classement public reste le nombre de victoires)
      this.score = sweep ? (ctx.data.progress.modeBests.boss ?? 0) : this.totalPhases * 1000 + Math.round((this.timeLeft ?? 0) * 10) + level * 100;
      if (!sweep) this.record(ctx, level + 1);
      const draws: NonNullable<ModeResult['rewards']['draws']> = [];
      if (!sweep) {
        draws.push({ pool: 'normal', count: 1 });
        if (timeFrac >= 0.4) draws.push({ pool: 'epic', count: 1 });
      }
      return { wallet: { dia: Math.round(50 * (1 + 0.12 * level)), starGem: 10 + Math.floor(level / 3) }, ...(draws.length > 0 ? { draws } : {}) };
    }
    // defaite : recompense proportionnelle aux PV retires (phases terminees comprises)
    const boss = this.boss(ctx);
    const removed = boss ? 1 - hpRatio(boss) : 0;
    this.score = Math.round(removed * this.totalPhases * 1000);
    return { wallet: { dia: Math.max(5, Math.round(40 * removed)) } };
  }
}

// ======================================================================================================================
// Raid
// ======================================================================================================================

interface Egg {
  id: number;
  age: number;
}

export class RaidMode extends GenericCombatMode<'raid'> {
  private bossId: number | null = null;
  private level = 1;
  private bossPhase = 0;
  private eggTimer = 0;
  private eggs: Egg[] = [];

  private get totalPhases(): number {
    return this.params.phaseAt.length + 1;
  }

  /** Niveau du raid demande (defaut : niveau suivant le meilleur deja battu). */
  private resolveLevel(ctx: SimContext): number {
    const wanted = this.startParams.level ?? (ctx.data.progress.modeBests.raid ?? 0) + 1;
    return Math.max(1, Math.min(Math.floor(wanted), 9999));
  }

  protected override onEnter(ctx: SimContext, params: ModeStartParams): void {
    super.onEnter(ctx, params);
    this.bossPhase = 0;
    this.eggTimer = 0;
    this.eggs = [];
    this.bossId = null;
    this.level = this.resolveLevel(ctx);
  }

  protected override spawnInitial(ctx: SimContext): void {
    const p = this.params;
    const hp = stageHp(ctx.config.balance, this.tierValue(ctx)).times(200 * Math.pow(p.bossHpPerLevel, Math.min(this.level - 1, p.maxScaleLevel)));
    const boss = spawnTarget(ctx, ctx.index.target('raid_dragon'), 0, 6, { stageValue: this.tierValue(ctx), hpOverride: hp, isBoss: true });
    this.bossId = boss.id;
    this.spawnedCount++;
    this.applySpeed(boss, 0);
    if (p.helperCat) ctx.services.team.addHelper();
  }

  private applySpeed(boss: With<'target' | 'health'>, i: number): void {
    if (!boss.mover) return;
    const base = baseSpeedOf(boss);
    boss.mover.speed = base * (this.params.phaseSpeedMult[Math.min(i, this.params.phaseSpeedMult.length - 1)] ?? 1);
  }

  private boss(ctx: SimContext): With<'target' | 'health'> | null {
    const e = this.bossId === null ? undefined : ctx.rt.world.get(this.bossId);
    return e && e.alive && e.target && e.health ? (e as With<'target' | 'health'>) : null;
  }

  protected override onRunning(ctx: SimContext, dt: number): void {
    super.onRunning(ctx, dt);
    if (this.sweeping) return;
    const boss = this.boss(ctx);
    if (!boss || (boss.visual?.dying ?? 0) > 0) return;
    const p = this.params;

    // phase d'apres les PV restants
    const ratio = hpRatio(boss);
    let next = 0;
    for (const t of p.phaseAt) if (ratio <= t) next++;
    if (next > this.bossPhase) {
      this.bossPhase = next;
      this.eggTimer = 1.5; // premiere ponte peu apres le changement de phase
      this.applySpeed(boss, this.bossPhase);
      ctx.bus.emit('BossPhase', { mode: 'raid', phase: this.bossPhase + 1, phases: this.totalPhases, shield: false });
    }

    // ponte
    const every = p.eggEvery[this.bossPhase] ?? 0;
    if (every > 0) {
      this.eggTimer -= dt;
      if (this.eggTimer <= 0) {
        this.eggTimer = every;
        this.layEggs(ctx, p.eggCount[this.bossPhase] ?? 1);
      }
    }

    // oeufs : vieillissent, eclosent (soin du dragon) s'ils survivent
    const heal = p.eggHealPct[this.bossPhase] ?? 0;
    this.eggs = this.eggs.filter((egg) => {
      const e = ctx.rt.world.get(egg.id);
      if (!e || !e.alive || (e.visual?.dying ?? 0) > 0) return false;
      egg.age += dt;
      if (egg.age < p.eggLifeSeconds) return true;
      boss.health.hp = boss.health.hp.plus(boss.health.max.times(heal));
      if (boss.health.hp.gt(boss.health.max)) boss.health.hp = boss.health.max;
      ctx.bus.emit('Explosion', { x: e.transform.x, y: e.transform.y, radius: 1.6, behavior: 'explosive' });
      ctx.bus.emit('Toast', { key: 'raid.hatch', kind: 'warning' });
      ctx.rt.targetGrid.remove(e.id);
      ctx.rt.world.destroy(e.id);
      return false;
    });
  }

  /** `count` oeufs autour du chef (portee de tir), dans l'arene. */
  private layEggs(ctx: SimContext, count: number): void {
    const leader = ctx.services.team.leader();
    const cx = leader?.transform.x ?? 0;
    const cy = leader?.transform.y ?? 0;
    const arena = ctx.modes.rules.arena;
    for (let i = 0; i < count; i++) {
      const a = ctx.rng.spawn.range(0, Math.PI * 2);
      const r = ctx.rng.spawn.range(3.5, 6.5);
      const pos = clampToArena({ x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r }, arena.shape, arena.halfW, arena.halfH, 1.5);
      const egg = spawnTarget(ctx, ctx.index.target('raid_egg'), pos.x, pos.y, { stageValue: this.tierValue(ctx), hpMult: this.params.eggHpMult });
      this.eggs.push({ id: egg.id, age: 0 });
      this.spawnedCount++;
    }
  }

  protected override hudExtras(ctx: SimContext): Partial<ModeHud> {
    const boss = this.boss(ctx);
    return {
      boss: boss ? { hp: boss.health.hp, max: boss.health.max } : null,
      bossInfo: { level: this.level, phase: this.bossPhase + 1, phases: this.totalPhases, shield: 0, eggs: this.eggs.length },
    };
  }

  protected override buildRewards(ctx: SimContext, success: boolean): ModeResult['rewards'] {
    const boss = this.boss(ctx);
    const done = success ? 1 : boss ? Math.max(0, 1 - hpRatio(boss)) : 0;
    const tiers = this.level - 1 + done;
    this.score = Math.round(tiers * 1000);
    if (success) this.record(ctx, this.level);
    const starGem = Math.round(this.params.starGemPerTier * (success ? this.level : tiers));
    return { wallet: { starGem }, ...(success && !this.sweeping ? { draws: [{ pool: 'epic' as const, count: 1 }] } : {}) };
  }
}

/** Vitesse de base du dragon (definition de la cible) : relue a chaque phase pour ne pas cumuler les multiplicateurs. */
const BASE_SPEED = new WeakMap<object, number>();
const baseSpeedOf = (boss: With<'target' | 'health'>): number => {
  const m = boss.mover;
  if (!m) return 1;
  let base = BASE_SPEED.get(m);
  if (base === undefined) {
    base = m.speed;
    BASE_SPEED.set(m, base);
  }
  return base;
};
